import { inject } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Route,
  RouterStateSnapshot,
  UrlMatcher,
  UrlSegment,
  UrlSegmentGroup,
} from '@angular/router';
import { Router } from '@angular/router';
import { OrganizationsService, OrganizationsStore } from '@cf/temp/services';
import { map } from 'rxjs';

const REDIRECTABLE_ROUTES = ['dashboard', 'events', 'meeting', 'groups'] as const;
type RedirectableRoute = (typeof REDIRECTABLE_ROUTES)[number];

export const buildRedirectUrl = (
  baseRoute: RedirectableRoute,
  segments: UrlSegment[],
  orgId: string,
): string => {
  const remainingPath = segments
    .slice(1)
    .map((segment) => segment.path)
    .join('/');
  return remainingPath
    ? `/org/${orgId}/${baseRoute}/${remainingPath}`
    : `/org/${orgId}/${baseRoute}`;
};

export const redirectMatcher: UrlMatcher = (
  segments: UrlSegment[],
  _group: UrlSegmentGroup,
  _route: Route,
) => {
  const firstSegment = segments[0]?.path;
  if (REDIRECTABLE_ROUTES.includes(firstSegment as RedirectableRoute)) {
    return { consumed: segments, posParams: {} };
  }
  return null;
};

export const redirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const orgStore = inject(OrganizationsStore);
  const orgService = inject(OrganizationsService);

  return orgService.fetchOrganizations().pipe(
    map((orgs) => {
      const selectedOrgId = orgStore.selectedEntityId() ?? orgs[0]?.id;
      if (!selectedOrgId) {
        // eslint-disable-next-line no-untranslated-string/no-untranslated-string
        throw new Error('No organization available for redirect');
      }

      const baseRoute = route.url[0].path as RedirectableRoute;

      orgStore.setOrganizations(orgs);

      const redirectUrl = buildRedirectUrl(baseRoute, route.url, String(selectedOrgId));

      router.navigate([redirectUrl], { queryParams: route.queryParams });

      return false;
    }),
  );
};
